import * as Sentry from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { isChunkLoadError } from '@/lib/chunkReload';

const environment = import.meta.env.MODE || 'development';

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
/**
 * True on a public token page: a survey (`/s/:token`) or a volunteer signup
 * page (`/v/:token`, whose `#fragment` may carry a personal access link).
 * Their URLs carry share-widely credentials.
 */
function isPublicTokenPage(): boolean {
  return typeof window !== 'undefined' && PUBLIC_PAGE_PREFIX_RE.test(window.location.pathname);
}

export function initSentry() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize Sentry if DSN is configured
  if (!sentryDsn) {
    console.warn('Sentry DSN not configured. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,

    // Set sample rates based on environment
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Never record a cold load of a public token page (`/s/`, `/v/`): replay
    // would start here, before the page mounts and calls `suspendSentryReplay()`,
    // and its first segment carries the raw `/s/<token>` URL (replay data
    // bypasses beforeSend / beforeSendTransaction).
    replaysSessionSampleRate: isPublicTokenPage() ? 0 : environment === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: isPublicTokenPage() ? 0 : 1.0,

    integrations: [
      // React Router v6 browser tracing - tracks route changes for proper page context
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // BLOCK IFRAMES — this keeps SIGNED STORAGE URLS out of Session Replay.
        //
        // `blockAllMedia` does NOT cover iframes. It expands to the recorder's
        // MEDIA_SELECTORS ('img,image,svg,video,object,picture,embed,map,audio,
        // link[rel=icon],link[rel=apple-touch-icon]') — no `iframe` in it. And
        // the recorder does not merely ignore an unblocked iframe's src, it
        // PRESERVES it: `if (tagName === 'iframe' && !keepIframeSrcFn(src))
        // { if (!needBlock && !getIframeContentDocument(n)) { rr_src = src } }`.
        // `keepIframeSrcFn` defaults to `() => false`, and a cross-origin frame
        // has no contentDocument, so both guards pass and the URL is recorded.
        //
        // We render attachment previews as `<iframe src={signedUrl}>` (see
        // InboxMessageDetail). A Supabase signed URL is a BEARER TOKEN: the
        // captured string is the org path, the verbatim filename, and an hour
        // of unauthenticated access to the bytes — readable by anyone with
        // access to the replay, on 10% of ordinary sessions and 100% of error
        // sessions. Images were never affected (`img` IS in MEDIA_SELECTORS).
        //
        // Blocking is what suppresses this: with `needBlock` true the recorder
        // skips the rr_src assignment AND deletes `src`. This composes with
        // `blockAllMedia` rather than replacing it — the option is concatenated
        // onto MEDIA_SELECTORS, not overwritten.
        //
        // `block` is the PUBLIC option (the rrweb-level `blockSelector` is not
        // on ReplayConfiguration); it is folded into blockSelector alongside
        // the .sentry-block defaults, and blockAllMedia concatenates onto that.
        //
        // Keep this even if the previews move to blob: URLs. It is the control
        // for the whole CLASS; a per-site fix leaves the next iframe exposed.
        block: ['iframe'],
      }),
    ],

    // Ignore common errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Auth errors (handled by app)
      'Invalid Refresh Token',
      'Auth session missing',
    ],

    beforeSend: sentryBeforeSend,

    // Sampling is tracesSampleRate's job; this hook only scrubs the public tokens.
    beforeSendTransaction: sentryBeforeSendTransaction,
  });
}

/**
 * beforeSend hook, exported for tests. Gates signed-out auth noise (downgrade,
 * never drop signed-in auth errors) and scrubs sensitive request data.
 */
export function sentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | null {
  // Don't send events in development (unless explicitly enabled)
  if (environment === 'development' && !import.meta.env.VITE_SENTRY_ENABLE_DEV) {
    return null;
  }

  // While signed out (explicit sign-out or failed token refresh), still-mounted
  // queries race the redirect to /login and can only produce auth failures and
  // `.single()` zero-row errors — expected noise. Downgrade to warning with a
  // dedicated fingerprint instead of dropping, so an unexpected spike (e.g. an
  // auth misconfiguration force-logging users out) stays queryable in Sentry
  // without polluting error alerting. Signed IN, the same errors mean a broken
  // auth-header attachment or an RLS regression: always captured, tagged
  // auth_error for alerting.
  const original = hint?.originalException;
  if (!sentryUserPresent && isSignedOutAuthNoise(original)) {
    event.level = 'warning';
    event.fingerprint = ['signed-out-auth-noise'];
    event.tags = { ...event.tags, auth_noise: true };
  } else if (isAuthError(original)) {
    event.tags = { ...event.tags, auth_error: true };
  }

  // A tab from before a deploy importing a chunk that is gone. The app reloads
  // itself (lib/chunkReload.ts), so this is expected after every deploy —
  // keep it queryable as one warning issue rather than a new error each time.
  if (isChunkLoadError(original)) {
    event.level = 'warning';
    event.fingerprint = ['stale-chunk-load'];
    event.tags = { ...event.tags, stale_chunk: true };
  }

  scrubEventUrls(event);

  return event;
}

/**
 * Scrub sensitive request data shared by error and transaction events:
 * cookies, credential-looking query params, and the public page tokens (and a
 * public page's `#fragment`) in the request URL and the fetch/xhr/navigation
 * breadcrumbs.
 */
function scrubEventUrls(event: Sentry.Event): void {
  if (event.request) {
    delete event.request.cookies;

    // Scrub query params that might contain sensitive data
    if (event.request.url) {
      try {
        const url = new URL(event.request.url);
        ['token', 'key', 'code', 'password', 'secret'].forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[REDACTED]');
          }
        });
        // A volunteer page's fragment is a personal access link: drop it with the token.
        if (PUBLIC_PAGE_PREFIX_RE.test(url.pathname)) url.hash = '';
        url.pathname = redactPublicToken(url.pathname);
        event.request.url = url.toString();
      } catch {
        // Not an absolute URL — still never send the token.
        event.request.url = redactPublicToken(event.request.url);
      }
    }
  }

  // Fetch/xhr/navigation breadcrumbs carry the same paths.
  for (const breadcrumb of event.breadcrumbs ?? []) {
    redactUrlFields(breadcrumb.data, ['url', 'from', 'to']);
  }
}

/** `TransactionEvent` lives in `@sentry/core`, which `@sentry/react` does not re-export. */
export type SentryTransactionEvent = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendTransaction']>>[0];

/** Span / breadcrumb data keys that can hold a URL or path. */
const SPAN_URL_KEYS = ['url', 'http.url', 'url.full', 'url.path', 'http.target'];

function redactUrlFields(data: Record<string, unknown> | undefined, keys: readonly string[]): void {
  if (!data) return;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string') data[key] = redactPublicToken(value);
  }
}

/**
 * beforeSendTransaction hook, exported for tests. Performance events carry
 * the page and API URLs too — the transaction name (a pageload before the
 * router parameterizes it is the raw `/s/<token>`), the request URL, and each
 * span's description (`GET https://…/api/v1/public/surveys/<token>`) and url
 * data. The public tokens are redacted from all of them (docs/SURVEYS.md,
 * docs/VOLUNTEER_SIGNUP.md).
 */
export function sentryBeforeSendTransaction(event: SentryTransactionEvent): SentryTransactionEvent {
  if (typeof event.transaction === 'string') event.transaction = redactPublicToken(event.transaction);
  scrubEventUrls(event);
  redactUrlFields(event.contexts?.trace?.data, SPAN_URL_KEYS);
  for (const span of event.spans ?? []) {
    if (typeof span.description === 'string') span.description = redactSpanDescription(span.description);
    redactUrlFields(span.data, SPAN_URL_KEYS);
  }
  return event;
}

/** A span description is a URL or path, optionally after an HTTP method (`GET /s/<token>`). */
function redactSpanDescription(description: string): string {
  const match = /^([A-Z]+ )(.*)$/s.exec(description);
  return match ? match[1] + redactPublicToken(match[2]) : redactPublicToken(description);
}

/**
 * Stop Sentry Session Replay while a page that must never be recorded is
 * mounted — the public survey answer page (`/s/:token`, docs/SURVEYS.md) and
 * the volunteer signup page (`/v/:token`). The
 * twin of `suspendPostHogRecording`. Stopping also ends on-error buffering for
 * the rest of this page load; replay is NOT restarted on unmount, because
 * `replay.start()` would record regardless of the configured sample rates.
 * Returns a no-op cleanup so it reads like an effect.
 */
export function suspendSentryReplay(): () => void {
  try {
    void Sentry.getReplay()?.stop().catch(() => undefined);
  } catch {
    // Replay not configured (no DSN) — nothing is recording.
  }
  return () => undefined;
}

// The public page tokens (`/s/<token>` surveys, `/v/<token>` volunteer
// signup pages, and the API paths those pages call, `/api/v1/public/<area>/<token>`)
// are share-widely credentials, and a volunteer page's `#fragment` carries a
// personal access link: never send either to Sentry (docs/SURVEYS.md,
// docs/VOLUNTEER_SIGNUP.md).
/** A public token page's path prefix (`/s/` or `/v/`). */
const PUBLIC_PAGE_PREFIX_RE = /^\/(?:s|v)\//;
/** Any public API path's token segment (`/api/v1/public/surveys/<token>`, `/api/v1/public/volunteer-signup/<token>`). */
const PUBLIC_API_TOKEN_RE = /\/api\/v1\/public\/([a-z][a-z-]*)\/[^/?#]+/g;
/** A public page URL or path: optional origin, `/s|v/<token>`, the rest without the fragment, then the fragment. */
const PUBLIC_PAGE_TOKEN_RE = /^((?:[a-z][a-z\d+.-]*:)?\/\/[^/?#]+)?\/(s|v)\/[^/?#]+([^#]*)(?:#.*)?$/is;

/**
 * Replace the public token in a URL or path with `[redacted]`, and drop a
 * public page's `#fragment`. Exported for tests.
 */
export function redactPublicToken(value: string): string {
  return value
    .replace(PUBLIC_API_TOKEN_RE, '/api/v1/public/$1/[redacted]')
    .replace(PUBLIC_PAGE_TOKEN_RE, '$1/$2/[redacted]$3');
}

/**
 * Whether a signed-in user is currently set on Sentry. Kept as a module flag
 * (updated by setSentryUser, which useAuth calls directly from
 * onAuthStateChange) so beforeSend can gate signed-out-noise filtering without
 * importing the Supabase client.
 */
let sentryUserPresent = false;

/**
 * onError for the app error boundary: offer Sentry's feedback dialog only to a
 * signed-in user. Anonymous visitors (the public survey page, sign-in) get the
 * plain fallback, and a stale chunk is reloading, not a crash to report.
 */
export function showReportDialogIfSignedIn(error: unknown, _componentStack: string, eventId: string): void {
  if (!sentryUserPresent || isChunkLoadError(error)) return;
  Sentry.showReportDialog({ eventId });
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email?: string } | null) {
  sentryUserPresent = user !== null;
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}

const AUTH_ERROR_CODES = new Set([
  'PGRST301', // PostgREST: JWT expired / invalid
  '42501', // Postgres insufficient_privilege (e.g. anon hitting a REVOKE'd RPC → 401)
]);

const AUTH_ERROR_MESSAGES = [
  'JWT',
  'Auth session missing',
  'Invalid Refresh Token',
  'Missing or malformed Authorization header', // Hono backend 401 envelope
  'permission denied',
];

/**
 * True for errors that mean "the caller is not (or no longer) authenticated":
 * HTTP 401s, 403s from supabase-js Auth/Functions errors, PostgREST
 * JWT/privilege codes, and the auth-shaped messages the Supabase client and
 * the Hono backend produce. A backend `ApiError` 403 is deliberately NOT an
 * auth error — those are deterministic authorization outcomes (feature flag
 * disabled, `Requires role: admin`), and tagging them auth_error would make a
 * rollout read as an auth outage. (Retry-skipping them is still right; the
 * QueryClient handles that case separately.) Used to skip pointless query
 * retries and to classify events in beforeSend. Matching here never
 * suppresses a signed-in error — that gate is auth *state*
 * (sentryUserPresent), not error shape.
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, code, message } = error as {
    status?: unknown;
    code?: unknown;
    message?: unknown;
  };
  if (status === 401) return true;
  if (status === 403 && !(error instanceof ApiError)) return true;
  if (typeof code === 'string' && AUTH_ERROR_CODES.has(code)) return true;
  if (typeof message !== 'string') return false;
  return AUTH_ERROR_MESSAGES.some((needle) => message.includes(needle));
}

/**
 * The full set of errors that are expected noise from a dead session's refetch
 * burst: auth errors plus PGRST116 (`.single()` matched 0 rows — inevitable
 * when RLS filters everything for a session that no longer exists). PGRST116
 * is only noise in the signed-out window; callers must combine this with the
 * sentryUserPresent gate, never use it alone.
 */
function isSignedOutAuthNoise(error: unknown): boolean {
  if (isAuthError(error)) return true;
  return (error as { code?: unknown } | null | undefined)?.code === 'PGRST116';
}

interface PostgrestErrorLike {
  message: string;
  code?: string | null;
  details?: string | null;
  hint?: string | null;
}

/**
 * Supabase PostgREST failures arrive as plain `{ code, details, hint, message }`
 * objects (postgrest-js returns the parsed response body verbatim), not Error
 * instances. Captured raw, Sentry titles every one of them "Object captured as
 * exception with keys: code, details, hint, message" and groups the entire
 * app's query failures into a single issue — and message-based filters
 * (ignoreErrors, beforeSend) can never match.
 */
function isPostgrestErrorLike(error: unknown): error is PostgrestErrorLike {
  if (!error || typeof error !== 'object' || error instanceof Error) return false;
  const candidate = error as Record<string, unknown>;
  return (
    typeof candidate.message === 'string' &&
    ('code' in candidate || 'details' in candidate || 'hint' in candidate)
  );
}

/**
 * Best available grouping scope from capture context: the query/mutation key
 * root when the capture came from the React Query caches, else the call-site
 * label conventions already used across the app (`source` / `context`).
 */
function scopeHint(context?: Record<string, unknown>): string | undefined {
  if (!context) return undefined;
  for (const key of ['queryKey', 'mutationKey']) {
    const value = context[key];
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  for (const key of ['source', 'context']) {
    const value = context[key];
    if (typeof value === 'string') return value;
  }
  return undefined;
}

/**
 * Wrap non-Error throwables into real Errors so Sentry gets a typed exception
 * instead of "Object captured as exception…". Postgrest-like objects keep
 * their code/details/hint (as extras and on the Error itself, so beforeSend
 * can classify them) and get a code+scope fingerprint — the synthesized stack
 * points here rather than at the failing query, so stack grouping would
 * otherwise collapse every failure into one issue again.
 */
function normalizeError(
  error: unknown,
  context?: Record<string, unknown>,
): { error: Error; extra?: Record<string, unknown>; fingerprint?: string[] } {
  if (error instanceof Error) return { error };

  if (isPostgrestErrorLike(error)) {
    const normalized = new Error(error.message) as Error & PostgrestErrorLike;
    normalized.name = 'PostgrestError';
    normalized.code = error.code;
    normalized.details = error.details;
    normalized.hint = error.hint;
    // Coerce the code: the guard doesn't verify its type, and a foreign
    // {message, code: <number>} throwable must not collapse to ['postgrest'].
    const codePart = error.code != null ? String(error.code) : error.message;
    const scope = scopeHint(context);
    const fingerprint = ['postgrest', codePart, ...(scope ? [scope] : [])];
    return {
      error: normalized,
      extra: { code: error.code, details: error.details, hint: error.hint },
      fingerprint,
    };
  }

  let message: string;
  try {
    message = typeof error === 'string' ? error : JSON.stringify(error);
  } catch {
    message = String(error);
  }
  return { error: new Error(message) };
}

/**
 * Capture an exception manually. Accepts anything thrown — plain Supabase
 * error objects are normalized into real Errors with stable fingerprints (see
 * normalizeError) so call sites can pass errors through without casting.
 */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  const { error: normalized, extra, fingerprint } = normalizeError(error, context);
  Sentry.captureException(normalized, {
    extra: { ...context, ...extra },
    ...(fingerprint ? { fingerprint } : {}),
  });
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, data?: Record<string, unknown>, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.addBreadcrumb({
    message,
    data,
    level,
  });
}
